import React from "react";
import { AlertCircle, CheckCircle2, Info, Loader2, X, XCircle } from "lucide-react";
import { cn } from "./utils";

const toneClasses = {
  info: "border-blue-200 bg-blue-50 text-blue-900",
  success: "border-emerald-200 bg-emerald-50 text-emerald-900",
  warning: "border-amber-200 bg-amber-50 text-amber-900",
  error: "border-red-200 bg-red-50 text-red-900"
};

const toneIcons = {
  info: Info,
  success: CheckCircle2,
  warning: AlertCircle,
  error: XCircle
};

export const Alert = ({ children, className, title, tone = "info" }) => {
  const Icon = toneIcons[tone] || Info;
  return (
    <div className={cn("ui-alert flex gap-3 rounded-md border p-3 text-sm", toneClasses[tone] || toneClasses.info, className)}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div>
        {title && <div className="font-semibold">{title}</div>}
        <div>{children}</div>
      </div>
    </div>
  );
};

export const Snackbar = ({
  action,
  autoHideDuration,
  className,
  isOpen,
  message,
  onClose,
  placement = "bottom",
  tone = "info"
}) => {
  React.useEffect(() => {
    if (!isOpen || !autoHideDuration || !onClose) return undefined;

    const timeoutId = window.setTimeout(() => onClose(), autoHideDuration);
    return () => window.clearTimeout(timeoutId);
  }, [autoHideDuration, isOpen, onClose]);

  if (!isOpen) return null;

  const placementClass = placement === "top" ? "top-5" : "bottom-5";

  return (
    <div className={cn("ui-snackbar fixed left-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-md border bg-white p-3 shadow-lg", placementClass, className)}>
      <div className="relative">
        <Alert className={onClose ? "pr-10" : undefined} tone={tone}>{message}</Alert>
        {onClose && (
          <button
            type="button"
            aria-label="Close notification"
            className="absolute right-2 top-2 rounded p-1 text-current opacity-70 transition hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
            onClick={onClose}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
      {action && <div className="mt-3 flex justify-end">{action}</div>}
    </div>
  );
};

export const Badge = ({ children, className, tone = "info" }) => (
  <span className={cn("ui-chip inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold", toneClasses[tone] || toneClasses.info, className)}>
    {children}
  </span>
);

export const Loader = ({ className, label = "Loading" }) => (
  <span className={cn("ui-loader inline-flex items-center gap-2 text-sm text-slate-600", className)}>
    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
    {label}
  </span>
);

export const Skeleton = ({ className }) => (
  <div className={cn("ui-skeleton animate-pulse rounded-md bg-slate-200", className)} />
);

export const Tooltip = ({ children, content }) => (
  <span title={content} className="ui-tooltip inline-flex">
    {children}
  </span>
);
